import { useState } from "react";
import { toast } from "sonner";
import { api, setToken, errMsg } from "@/lib/api";
import { IMAGES } from "@/data/assets";
import { LogIn, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

const field =
  "w-full bg-[#0e0e0e] border border-white/12 px-4 py-3.5 text-white placeholder:text-white/30 text-sm outline-none focus:border-[#e31e24] transition-colors";

export const AdminLogin = ({ onSuccess }) => {
  const [step, setStep] = useState("creds"); // creds | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [preToken, setPreToken] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [busy, setBusy] = useState(false);

  const finish = (data) => {
    setToken(data.access_token);
    toast.success("Welcome back, Raymond.");
    onSuccess();
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.login(email, password);
      if (data.otp_required) {
        setPreToken(data.pre_token);
        setPhoneHint(data.phone_hint || "");
        setStep("otp");
        setCode("");
        toast.success("Verification code sent by SMS.");
      } else {
        finish(data);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.verifyOtp(preToken, code.trim());
      finish(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.login(email, password);
      if (data.otp_required) {
        setPreToken(data.pre_token);
        toast.success("New code sent.");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100svh] grid place-items-center px-5 relative rp-grain">
      <div className="absolute inset-0 rp-red-glow opacity-60 pointer-events-none" />

      {step === "creds" ? (
        <form
          onSubmit={submitCreds}
          noValidate
          className="relative w-full max-w-sm bg-[#141414] border border-white/10 p-8"
          data-testid="admin-login-form"
        >
          <img src={IMAGES.logo} alt="Rayce Parts" className="h-6 w-auto mb-8" />
          <h1 className="font-display text-4xl text-white mb-1">Admin</h1>
          <p className="text-white/50 text-sm mb-7">
            Sign in to manage enquiries, orders & gallery.
          </p>

          <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block">
            Email
          </label>
          <input
            type="email"
            className={field}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@rayceparts.com"
            data-testid="admin-email"
          />

          <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block mt-4">
            Password
          </label>
          <input
            type="password"
            className={field}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password"
          />

          <button
            type="submit"
            disabled={busy}
            className="btn-race w-full justify-center mt-7 disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            {busy ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <LogIn size={16} />
            )}
            {busy ? "Checking…" : "Continue"}
          </button>
          <p className="mt-4 font-mono-rp text-[10px] text-white/35 text-center">
            Protected by SMS two-factor authentication.
          </p>
        </form>
      ) : (
        <form
          onSubmit={submitOtp}
          noValidate
          className="relative w-full max-w-sm bg-[#141414] border border-white/10 p-8"
          data-testid="admin-otp-form"
        >
          <div className="grid place-items-center h-12 w-12 bg-[#e31e24]/15 border border-[#e31e24]/40 mb-6">
            <ShieldCheck size={22} className="text-[#e31e24]" />
          </div>
          <h1 className="font-display text-4xl text-white mb-1">Verify</h1>
          <p className="text-white/50 text-sm mb-7">
            Enter the 6-digit code we texted to{" "}
            <span className="text-white/80">{phoneHint || "your phone"}</span>.
          </p>

          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className={field + " text-center tracking-[0.5em] text-xl"}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="••••••"
            data-testid="admin-otp-input"
            autoFocus
          />

          <button
            type="submit"
            disabled={busy}
            className="btn-race w-full justify-center mt-6 disabled:opacity-60"
            data-testid="admin-otp-verify-btn"
          >
            {busy ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ShieldCheck size={16} />
            )}
            {busy ? "Verifying…" : "Verify & Sign In"}
          </button>

          <div className="flex items-center justify-between mt-5">
            <button
              type="button"
              onClick={() => setStep("creds")}
              className="flex items-center gap-1.5 font-mono-rp text-[11px] uppercase tracking-[0.15em] text-white/40 hover:text-white transition-colors"
              data-testid="admin-otp-back"
            >
              <ArrowLeft size={13} /> Back
            </button>
            <button
              type="button"
              onClick={resend}
              disabled={busy}
              className="font-mono-rp text-[11px] uppercase tracking-[0.15em] text-white/40 hover:text-[#e31e24] transition-colors disabled:opacity-50"
              data-testid="admin-otp-resend"
            >
              Resend code
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
